import { useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import EditIcon from '@mui/icons-material/Edit';

import {
  drillFragmentData,
  getFragmentData,
  graphql,
} from '@e-socna/data-layer';

import { useMissionAccess } from '../../components/Access/hooks/useMissionAccess.hook';
import { QuotationLockNotice } from '../../components/Access/QuotationLockNotice';
import { useMissionSpaceWording } from '../../hooks/useMissionSpaceWording.hook';
import { RightDrawer } from '../../components/RightDrawer';
import { FormVariant } from '../../forms';
import { AddressWithMap } from '../../components/AddressWithMap';
import { MissionForm } from '../../forms/Mission';
import { MissionFormTab } from '../../forms/Mission/enums/tab.enum';
import { useMissionReadyContext } from '../../contexts/MissionContext/hooks/useMissionReadyContext.hook';

import { FolderBusinessEntity_FolderBusinessEntityConsolidation_Fragment } from '../Folder/fragments/folder-business-entity-consolidation.fragment';

import { MissionPage_Tabs_Mission_Fragment } from './Tabs';

export const MissionPage_Address_Mission_Fragment = graphql(`
  fragment MissionPage_Address_Mission_Fragment on Mission {
    _id

    consolidation {
      address {
        ...AddressWithMapCmp_Address_Fragment
      }
      isAddressFallback
    }

    ...MissionForm_Mission_Fragment
  }
`);

interface Props {
  readonly paths: Readonly<{
    consult: string;
    edit: string;
  }>;
}

export const Address = ({ paths }: Readonly<Props>) => {
  const { mission: rawMission, folder } = useMissionReadyContext();

  const mission = drillFragmentData(
    [MissionPage_Tabs_Mission_Fragment, MissionPage_Address_Mission_Fragment],
    rawMission,
  );

  const folderConsolidation = getFragmentData(
    FolderBusinessEntity_FolderBusinessEntityConsolidation_Fragment,
    folder.consolidation,
  );

  const {
    consolidation: { address, isAddressFallback },
  } = mission;

  const { pathname: currentPath } = useLocation();
  const navigate = useNavigate();

  const isInEditionMode = currentPath === paths.edit;
  const leaveEditionMode = useCallback(
    () => void navigate(paths.consult),
    [navigate, paths.consult],
  );

  const access = useMissionAccess(mission._id, mission);
  const wording = useMissionSpaceWording();

  return (
    <>
      <QuotationLockNotice
        gate={access.updateQuotationRelatedFields}
        lockedFields="son adresse ne peut plus changer"
        wording={wording}
      />
      <AddressWithMap
        address={isAddressFallback ? undefined : address}
        editAction={{
          id: 'editMissionAddress',
          gate: access.updateAddress,
          icon: <EditIcon />,
          name: 'Modifier Adresse',
          to: paths.edit,
        }}
        // The inherited address is the consolidated one: the collaboration
        // one for a sub-mission.
        fallbackAddress={
          isAddressFallback ? address : folderConsolidation.address
        }
        id={mission._id}
        sx={{ mt: 4 }}
      />
      <RightDrawer isOpen={isInEditionMode} onDrawerClose={leaveEditionMode}>
        <MissionForm
          folderConsolidation={folder.consolidation}
          formVariant={FormVariant.Modification}
          initialValues={mission}
          missionId={mission._id}
          onCancel={leaveEditionMode}
          onSuccess={leaveEditionMode}
          stepIdsSubset={[MissionFormTab.Address]}
          title={`Formulaire de modification de l'Adresse ${wording.ofName}`}
        />
      </RightDrawer>
    </>
  );
};
